/*!
 * ScrollTrigger Animation Library - JavaScript
 * Uses IntersectionObserver with rendering fallback
 * Version: 1.0.2
 */
(function () {
    "use strict";

    const config = {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
        triggerClass: "triggered",
        fallbackClass: "scrolltrigger-fallback",
        fallbackDelay: 800,
        rearmBuffer: 300
    };

    const animationClasses = [
        "fade-in",
        "slide-up",
        "slide-down",
        "slide-left",
        "slide-right",
        "zoom-in",
        "zoom-out",
        "scale-up",
        "rotate-in",
        "bounce-in",
        "showsolitext",
        "image-animation"
    ];

    const selector = animationClasses
        .map(cls => `.${cls}`)
        .join(", ");

    let observer = null;
    let scrollTicking = false;
    let fallbackTimer = null;

    const triggeredElements = new WeakSet();
    const pendingElements = new WeakSet();
    const pendingTimers = new WeakMap();

    /*
     * Unlike WeakSet, this must be iterable because fallback
     * elements need to be re-armed when a real user scrolls.
     */
    const fallbackElements = new Set();

    function init() {
    injectFallbackCSS();

        if (window.innerWidth <= 767) {
            document.querySelectorAll(".image-animation").forEach(element => {
                element.classList.add(
                    config.triggerClass,
                    config.fallbackClass
                );

                triggeredElements.add(element);
            });
        }
        const prefersReducedMotion =
            window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
            !window.forceScrollTrigger;

        /*
         * Reduced motion:
         * content should simply remain visible.
         */
        if (prefersReducedMotion) {
            revealEverything();
            setupMutationObserver();
            return;
        }

        /*
         * Old/unsupported browser:
         * never leave content hidden.
         */
        if (!("IntersectionObserver" in window)) {
            revealEverything();
            setupMutationObserver();
            return;
        }

        observer = new IntersectionObserver(
            handleIntersection,
            {
                threshold: config.threshold,
                rootMargin: config.rootMargin
            }
        );

        observeElements();
        setupMutationObserver();
        setupScrollFallback();

        /*
         * Important:
         *
         * After a short delay, reveal anything that IntersectionObserver
         * has not reached yet.
         *
         * This allows:
         * - full-page screenshots
         * - crawlers
         * - direct anchor jumps
         * - automated rendering tools
         *
         * to see the content.
         */
        fallbackTimer = setTimeout(
            revealUntriggeredElements,
            config.fallbackDelay
        );
    }

    function injectFallbackCSS() {
        if (
            document.getElementById(
                "scrolltrigger-fallback-style"
            )
        ) {
            return;
        }

        const style =
            document.createElement("style");

        style.id =
            "scrolltrigger-fallback-style";

        style.textContent = `
            .${config.fallbackClass} {
                transition: none !important;
                animation: none !important;
            }
        `;

        document.head.appendChild(style);
    }

    function handleIntersection(entries) {
        entries.forEach(entry => {
            if (!entry.isIntersecting) {
                return;
            }

            const element = entry.target;

            /*
             * If this element was revealed by the fallback
             * but the user has now reached it directly,
             * leave it visible.
             */
            if (fallbackElements.has(element)) {
                element.classList.remove(
                    config.fallbackClass
                );

                fallbackElements.delete(element);

                if (observer) {
                    observer.unobserve(element);
                }

                return;
            }

            if (
                triggeredElements.has(element) ||
                pendingElements.has(element)
            ) {
                return;
            }

            const customDelay =
                parseFloat(element.dataset.delay) * 1000 || 0;

            const staggerDelay =
                parseInt(element.dataset.staggerDelay) || 0;

            const totalDelay =
                customDelay + staggerDelay;

            pendingElements.add(element);

            const timer = setTimeout(() => {
                pendingElements.delete(element);
                pendingTimers.delete(element);

                triggerElement(element);
            }, totalDelay);

            pendingTimers.set(element, timer);

            if (
                element.classList.contains("trigger-once") &&
                observer
            ) {
                observer.unobserve(element);
            }
        });
    }

    function triggerElement(element) {
        if (
            !element ||
            triggeredElements.has(element)
        ) {
            return;
        }

        element.classList.remove(
            config.fallbackClass
        );

        fallbackElements.delete(element);

        element.classList.add(
            config.triggerClass
        );

        triggeredElements.add(element);

        element.dispatchEvent(
            new CustomEvent(
                "scrolltrigger:triggered",
                {
                    detail: {
                        element: element
                    }
                }
            )
        );
    }

    /*
     * Used by screenshot/crawler fallback.
     *
     * It visually reveals content but deliberately DOES NOT
     * fire scrolltrigger:triggered.
     */
    function fallbackReveal(element) {
        if (
            !element ||
            triggeredElements.has(element) ||
            pendingElements.has(element)
        ) {
            return;
        }

        element.classList.add(
            config.fallbackClass
        );

        element.classList.add(
            config.triggerClass
        );

        triggeredElements.add(element);
        fallbackElements.add(element);

        if (observer) {
            observer.unobserve(element);
        }
    }

    function revealUntriggeredElements() {
        document
            .querySelectorAll(selector)
            .forEach(element => {
                if (
                    triggeredElements.has(element) ||
                    pendingElements.has(element)
                ) {
                    return;
                }

                fallbackReveal(element);
            });
    }

    function revealEverything() {
        document
            .querySelectorAll(selector)
            .forEach(element => {
                element.classList.add(
                    config.fallbackClass
                );

                element.classList.add(
                    config.triggerClass
                );

                triggeredElements.add(element);
            });
    }

    /*
     * When a real visitor starts scrolling, restore the normal
     * hidden animation state for fallback-revealed elements
     * that are still safely below the viewport.
     *
     * This means they can animate normally when the visitor
     * actually reaches them.
     */
    function rearmFallbackElements() {
        fallbackElements.forEach(element => {
            const rect =
                element.getBoundingClientRect();

            const safelyBelowViewport =
                rect.top >
                window.innerHeight +
                config.rearmBuffer;

            /*
             * Still far below the screen:
             * restore original animation behavior.
             */
            if (safelyBelowViewport) {
                element.classList.remove(
                    config.fallbackClass
                );

                element.classList.remove(
                    config.triggerClass
                );

                triggeredElements.delete(element);
                fallbackElements.delete(element);

                const staggerContainer =
                    element.closest(
                        ".stagger-children"
                    );

                if (staggerContainer) {
                    setupStaggerElement(
                        element,
                        staggerContainer
                    );
                }

                if (observer) {
                    observer.observe(element);
                }

                return;
            }

            /*
             * User jumped directly to or near this element.
             * Do not hide it again, otherwise we recreate the
             * blank-content problem.
             */
            const nearOrInsideViewport =
                rect.top <=
                window.innerHeight +
                config.rearmBuffer;

            if (nearOrInsideViewport) {
                element.classList.remove(
                    config.fallbackClass
                );

                fallbackElements.delete(element);

                if (observer) {
                    observer.unobserve(element);
                }
            }
        });
    }

    function handleRealScroll() {
        if (scrollTicking) {
            return;
        }

        scrollTicking = true;

        requestAnimationFrame(() => {
            rearmFallbackElements();
            scrollTicking = false;
        });
    }

    function setupScrollFallback() {
        window.addEventListener(
            "scroll",
            handleRealScroll,
            {
                passive: true
            }
        );

        window.addEventListener(
            "touchmove",
            handleRealScroll,
            {
                passive: true
            }
        );

        window.addEventListener(
            "wheel",
            handleRealScroll,
            {
                passive: true
            }
        );
    }

    function observeElements() {
        document
            .querySelectorAll(selector)
            .forEach(element => {
                if (
                    triggeredElements.has(element) ||
                    pendingElements.has(element)
                ) {
                    return;
                }

                const staggerContainer =
                    element.closest(
                        ".stagger-children"
                    );

                if (staggerContainer) {
                    setupStaggerElement(
                        element,
                        staggerContainer
                    );
                }

                if (observer) {
                    observer.observe(element);
                }
            });
    }

    function setupStaggerElement(
        element,
        container
    ) {
        if (
            element.closest(
                ".text-blurbs-swiper"
            ) ||
            element.classList.contains(
                "swiper-animation"
            )
        ) {
            return;
        }

        const staggerSelector =
            animationClasses
                .map(cls => `.${cls}`)
                .join(", ");

        const elements = Array.from(
            container.querySelectorAll(
                staggerSelector
            )
        ).filter(el =>
            !el.closest(
                ".text-blurbs-swiper"
            ) &&
            !el.classList.contains(
                "swiper-animation"
            )
        );

        const elementIndex =
            elements.indexOf(element);

        if (elementIndex === -1) {
            return;
        }

        const customDelay =
            parseInt(
                container.dataset.staggerDelay
            ) || 120;

        const staggerDelay =
            elementIndex * customDelay;

        element.dataset.staggerDelay =
            staggerDelay.toString();

        element.classList.add(
            "stagger-item"
        );
    }

    function setupMutationObserver() {
        new MutationObserver(mutations => {
            let shouldUpdate = false;

            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (
                        node.nodeType !==
                        Node.ELEMENT_NODE
                    ) {
                        return;
                    }

                    const hasAnimClass =
                        animationClasses.some(
                            cls =>
                                node.classList &&
                                node.classList.contains(
                                    cls
                                )
                        );

                    const hasAnimChildren =
                        node.querySelectorAll &&
                        animationClasses.some(
                            cls =>
                                node.querySelectorAll(
                                    `.${cls}`
                                ).length > 0
                        );

                    if (
                        hasAnimClass ||
                        hasAnimChildren
                    ) {
                        shouldUpdate = true;
                    }
                });
            });

            if (!shouldUpdate) {
                return;
            }

            setTimeout(() => {
                observeElements();
            }, 100);
        }).observe(
            document.body,
            {
                childList: true,
                subtree: true
            }
        );
    }

    const ScrollTrigger = {
        trigger: function (target) {
            const elements =
                typeof target === "string"
                    ? document.querySelectorAll(
                        target
                    )
                    : [target];

            elements.forEach(element => {
                const timer =
                    pendingTimers.get(
                        element
                    );

                if (timer) {
                    clearTimeout(timer);

                    pendingTimers.delete(
                        element
                    );

                    pendingElements.delete(
                        element
                    );
                }

                triggerElement(element);
            });
        },

        reset: function (target) {
            const elements =
                typeof target === "string"
                    ? document.querySelectorAll(
                        target
                    )
                    : [target];

            elements.forEach(element => {
                const timer =
                    pendingTimers.get(
                        element
                    );

                if (timer) {
                    clearTimeout(timer);

                    pendingTimers.delete(
                        element
                    );
                }

                pendingElements.delete(
                    element
                );

                fallbackElements.delete(
                    element
                );

                element.classList.remove(
                    config.fallbackClass
                );

                element.classList.remove(
                    config.triggerClass
                );

                triggeredElements.delete(
                    element
                );

                if (observer) {
                    observer.observe(element);
                }
            });
        },

        refresh: function () {
            observeElements();
        },

        destroy: function () {
            clearTimeout(
                fallbackTimer
            );

            window.removeEventListener(
                "scroll",
                handleRealScroll
            );

            window.removeEventListener(
                "touchmove",
                handleRealScroll
            );

            window.removeEventListener(
                "wheel",
                handleRealScroll
            );

            if (observer) {
                observer.disconnect();
                observer = null;
            }
        },

        version: "1.0.2"
    };

    if (
        document.readyState === "loading"
    ) {
        document.addEventListener(
            "DOMContentLoaded",
            init
        );
    } else {
        init();
    }

    window.ScrollTrigger =
        ScrollTrigger;

    if (
        typeof module !== "undefined"
    ) {
        module.exports =
            ScrollTrigger;
    }
})();